import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveSupplier,
  usePendingSuppliers,
} from "../hooks/useApprovals";
import type { Supplier } from "../services/suppliersApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const SupplierApprovalsPage = () => {
  const notify = useNotification();
  const pendingQuery = usePendingSuppliers();
  const approveMutation = useApproveSupplier();

  const [selected, setSelected] = useState<Supplier | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  const handleApprove = (s: Supplier) => {
    approveMutation.mutate(
      { id: s.id, request: { isApproved: true } },
      {
        onSuccess: () => notify.success(`Supplier "${s.supplierName}" approved.`),
        onError: () => notify.error("Failed to approve supplier."),
      }
    );
  };

  const openReject = (s: Supplier) => {
    setSelected(s);
    setRejectOpen(true);
  };

  const handleReject = (reason: string) => {
    if (!selected) return;
    approveMutation.mutate(
      { id: selected.id, request: { isApproved: false, rejectionReason: reason } },
      {
        onSuccess: () => {
          notify.success(`Supplier "${selected.supplierName}" rejected.`);
          setRejectOpen(false);
          setSelected(null);
        },
        onError: () => notify.error("Failed to reject supplier."),
      }
    );
  };

  const rows = pendingQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Supplier Approvals</h1>
        <p className="mt-1 text-muted-foreground">
          Review suppliers awaiting GM approval. Approved suppliers become selectable
          on inputs and stock receipts; rejected rows freeze for audit.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="inventory.approve_supplier" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending Suppliers</CardTitle>
            {rows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                {rows.length}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No suppliers awaiting approval.
            </p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>County</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.supplierName}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {s.supplierCode || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {s.contactPerson || "—"}
                        {s.phoneNumber && (
                          <span className="text-xs text-muted-foreground block">
                            {s.phoneNumber}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{s.county || "—"}</TableCell>
                      <TableCell className="text-sm">{formatDate(s.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(s)}
                            disabled={approveMutation.isPending}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => openReject(s)}
                            disabled={approveMutation.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Supplier"
        description="The maker will be notified with this reason and can create a corrected supplier."
        reasonLabel="Rejection Reason"
        reasonPlaceholder="e.g. Tax ID does not match KRA records"
        submitLabel="Reject"
        isSubmitting={approveMutation.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
